<script>
import DragAndDrop from './DragAndDrop.vue';
import FireCrawl from './FireCrawl.vue';
import DeepCrawl from './DeepCrawl.vue';
import GoogleDrivePicker from './GoogleDrivePicker.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'UploadDocumentDialog',
  components: {
    DragAndDrop,
    FireCrawl,
    GoogleDrivePicker,
    DeepCrawl
  },
  props: {
    datasetId: {
      type: String,
      required: true
    },
    existingWordCount: {
      type: Number,
      required: true
    },
    engine: {
      type: String,
      default: 'dify'
    }
  },
  computed: {
    ...mapState('knowledge', ['crawlResult', 'uploadedFile', 'uploadedFiles']),
    ...mapGetters('stripe', ['getWordsLimit']),
    isLightrag() {
      return this.engine === 'lightrag';
    },
    isChunkOverlapInvalid() {
      return (
        this.splitMethod === 'chunk' &&
        this.chunkWordSize !== null &&
        this.chunkWordSize !== '' &&
        this.chunkOverlapWordSize !== null &&
        this.chunkOverlapWordSize !== '' &&
        Number(this.chunkOverlapWordSize) >= Number(this.chunkWordSize)
      );
    },
    isDelimiterInvalid() {
      return (
        this.splitMethod === 'delimiter' &&
        !!this.delimiter &&
        this.delimiter.length > 10
      );
    },
    wordLimit() {
      return this.getWordsLimit || 100000;
    },
    remainingWordLimit() {
      return this.wordLimit - this.existingWordCount;
    },
    isExistingWordLimitExceeded() {
      return this.existingWordCount >= this.wordLimit;
    },
    selectedWordCount() {
      const selected = this.selectedResult.reduce((sum, item) => {
        return sum + this.countUniversal(item.markdown || '');
      }, 0);

      return selected + this.existingWordCount; // include existing words
    },
    isWordLimitExceeded() {
      return this.selectedWordCount >= this.wordLimit;
    }
  },
  data() {
    return {
      source: 'file',
      selectedResult: [],
      isUploading: false,
      expanded: {},
      editContent: {},
      saving: {},
      // Chunk-size/overlap and delimiter are mutually exclusive splitting
      // strategies — only the selected method's value is ever sent.
      splitMethod: 'chunk',
      chunkWordSize: 900,
      chunkOverlapWordSize: 75,
      // Literal escape text, not an actual newline byte — a raw newline is
      // invisible/stripped in a single-line text input. Resolved back to a
      // real newline in the store before it's sent (knowledge.js).
      delimiter: '\\n'
    }
  },
  methods: {
    ...mapActions('knowledge', [
      'handleUpdateCrawlResult',
      'handleRemoveFile',
      'updateIsUploaded',
      'handleUpload',
      'handleFileUpload',
      'updateAdvancedUploadOptions'
    ]),
    setSplitMethod(method) {
      this.splitMethod = method;
      // Clear the other method's fields so only one is ever sent.
      if (method === 'chunk') {
        this.delimiter = null;
        if (this.chunkWordSize === null) {
          this.chunkWordSize = 900;
        }
        if (this.chunkOverlapWordSize === null) {
          this.chunkOverlapWordSize = 75;
        }
      } else {
        this.chunkWordSize = null;
        this.chunkOverlapWordSize = null;
        if (this.delimiter === null) {
          this.delimiter = '\\n';
        }
      }
      this.syncAdvancedUploadOptions();
    },
    syncAdvancedUploadOptions() {
      // Gate strictly on splitMethod, not just on whichever fields happen to
      // be non-null — the two methods' fields default independently and
      // aren't guaranteed to be cleared, so this is the one place that must
      // guarantee only one method's value is ever sent.
      this.updateAdvancedUploadOptions({
        chunkWordSize: this.splitMethod === 'chunk' ? this.chunkWordSize : null,
        chunkOverlapWordSize:
          this.splitMethod === 'chunk' ? this.chunkOverlapWordSize : null,
        delimiter: this.splitMethod === 'delimiter' ? this.delimiter : null,
      });
    },
    isCJK(char) {
      const code = char.codePointAt(0)
      return (
        (code >= 0x4e00 && code <= 0x9fff) ||   // Chinese
        (code >= 0x3400 && code <= 0x4dbf) ||   // CJK Ext A
        (code >= 0x3040 && code <= 0x309f) ||   // Hiragana
        (code >= 0x30a0 && code <= 0x30ff) ||   // Katakana
        (code >= 0xac00 && code <= 0xd7af) ||   // Hangul
        (code >= 0x0e80 && code <= 0x0eff) ||   // Lao
        (code >= 0x1780 && code <= 0x17ff)      // Khmer
      )
    },
    isEmoji(char) {
      return /\p{Extended_Pictographic}/u.test(char)
    },
    countUniversal(text) {
      let cjkCount = 0
      let emojiCount = 0
      let buffer = ''

      for (const ch of text) {
        if (this.isCJK(ch)) {
          cjkCount++
          buffer += ' '
        } else if (this.isEmoji(ch)) {
          emojiCount++
          buffer += ' '
        } else {
          buffer += ch
        }
      }

      const words = buffer.match(/[A-Za-z]+|\d+/g) || []
      const wordCount = words.length

      return cjkCount + emojiCount + wordCount
    },
    canSelect(result) {
      if (this.selectedResult.includes(result)) return true;
      const nextTotal = this.selectedWordCount + this.countUniversal(result.markdown || '');
      return nextTotal <= (this.wordLimit - this.existingWordCount);
    },
    changeSource(source) {
      this.source = source;
    },
    handleCancel() {
      this.handleRemoveFile();
      this.$emit('cancel');
    },
    async handleUploadLocal() {
      try {
        this.isUploading = true;
        await this.handleUpload(this.datasetId);
        this.handleFileUpload(null);
        this.$emit('success');
      } catch(err) {
        console.error(err);
      } finally {
        this.isUploading = false;
      }
    },
    async handleUploadCrawl() {
      if (this.selectedResult.length === 0) {
        useAlert(this.$t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.SELECT_PAGE_ERROR'));
        return;
      }

      try {
        console.log(this.selectedResult)
        this.isUploading = true;
        this.handleUpdateCrawlResult(this.selectedResult);
        await this.handleUpload(this.datasetId);
        this.$emit('success');
      } catch(err) {
        console.error(err);
      } finally {
        this.isUploading = false;
      }
    },
    handleScrollDown() {
      this.$nextTick(() => {
        const resultsEl = this.$refs.resultsList;
        if (resultsEl) {
          resultsEl.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }
      });
    },
    toggleExpanded(index) {
      this.$set(this.expanded, index, !this.expanded[index]);
      if (this.expanded[index] && !this.editContent[index]) {
        this.$set(this.editContent, index, this.crawlResult.data[index].markdown || '');
      }
    },
    isEditing(index) {
      return !!this.expanded[index];
    },
    currentWordCount(index) {
      const content = this.expanded[index] && this.editContent[index] ? this.editContent[index] : this.crawlResult.data[index].markdown;
      return content ? this.countUniversal(content) : 0;
    },
    resultKey(result, index) {
      return `${result.metadata?.title || ''}-${index}`;
    },
    saveEdit(index) {
      this.$set(this.saving, index, true);
      
      const updatedResult = { ...this.crawlResult.data[index] };
      updatedResult.markdown = this.editContent[index];
      
      const newData = [...this.crawlResult.data];
      newData[index] = updatedResult;

      this.$store.commit('knowledge/setCrawlResult', { data: [...newData] });
      
      setTimeout(() => {
        this.$set(this.expanded, index, false);
        this.$set(this.saving, index, false);
      }, 300);
    },
    cancelEdit(index) {
      this.$set(this.expanded, index, false);
      this.$set(this.editContent, index, null);
    }
  },
  watch: {
    crawlResult: {
      handler(result) {
        if (!result?.data) return;

        // Reset selectedResult to empty array first to clear old selections
        this.selectedResult = [];
        
        // Then auto-select items that fit within remaining word limit
        this.$nextTick(() => {
          if (!this.isExistingWordLimitExceeded) {
            const selected = [];
            let total = 0;
            const remainingLimit = this.wordLimit - this.existingWordCount;

            for (const item of result.data) {
              const len = item.markdown?.length || 0;

              if (total + len > remainingLimit) break;

              selected.push(item);
              total += len;
            }

            this.selectedResult = selected;
          }
        });
        
        // Also reset expanded and editContent to clear any editing state from previous crawl
        this.expanded = {};
        this.editContent = {};
      },
      deep: true
    }
  },
  mounted() {
    this.handleRemoveFile();
    this.updateIsUploaded(false);
    // Push the suggested defaults to the store now, otherwise they'd only
    // apply once the user touches a field or switches split method.
    this.syncAdvancedUploadOptions();
  }
}
</script>

<template>
  <div>
    <!-- Backdrop -->
    <div class="fixed top-0 left-0 h-[100vh] w-[100vw] bg-slate-800/50 dark:bg-slate-100/50" @click="handleCancel"></div>

    <!-- Dialog box -->
    <div 
      class="z-50 bg-slate-100 dark:bg-slate-900 rounded-md flex flex-col p-6 absolute top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 w-4/5 max-w-2xl max-h-[80vh] overflow-auto"
    >
      <h1 class="text-xl mb-4 text-slate-900 dark:text-slate-100">{{ $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.TITLE') }}</h1>

      <!-- Source selection -->
      <div class="flex gap-2 mb-6 flex-wrap">
        <button 
          @click="changeSource('file')" 
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'file',
            'border-slate-200 dark:border-slate-200': source !== 'file'
          }" 
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'>
          <fluent-icon icon="file-upload" class="h-[30px] w-[30px]" />
          {{ $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.IMPORT_FILE') }}
        </button>
        <button 
          @click="changeSource('gdrive')" 
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'gdrive',
            'border-slate-200 dark:border-slate-200': source !== 'gdrive'
          }" 
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'>
          <img src="/assets/images/auth/google.svg" alt="Google Drive" class="h-[25px] w-[25px]" />          {{ $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.IMPORT_GDRIVE') }}
        </button>
        <!-- <button 
          @click="changeSource('website')" 
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'website',
            'border-slate-200 dark:border-slate-200': source !== 'website'
          }" 
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'>
          <fluent-icon icon="globe" class="h-[25px] w-[25px]" />
          {{ $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.SYNC_WEBSITE') }}
        </button> -->
        <button 
          @click="changeSource('deepcrawl')" 
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'deepcrawl',
            'border-slate-200 dark:border-slate-200': source !== 'deepcrawl'
          }" 
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'>
          <fluent-icon icon="globe" class="h-[25px] w-[25px]" />
          {{ $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.SYNC_WEBSITE') }}
        </button>
      </div>

      <!-- Advanced options — lightrag only, ignored by the server for dify -->
      <div class="mb-4" v-if="isLightrag">
        <div class="p-4 border border-slate-200 dark:border-slate-700 rounded-md">
          <div>
            <label class="block text-sm font-medium mb-1">{{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.SPLIT_METHOD_LABEL')
            }}</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="splitMethodDialog"
                  value="chunk"
                  :checked="splitMethod === 'chunk'"
                  @change="setSplitMethod('chunk')"
                />
                {{
                  $t(
                    'KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.SPLIT_METHOD_CHUNK_LABEL'
                  )
                }}
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="splitMethodDialog"
                  value="delimiter"
                  :checked="splitMethod === 'delimiter'"
                  @change="setSplitMethod('delimiter')"
                />
                {{
                  $t(
                    'KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.SPLIT_METHOD_DELIMITER_LABEL'
                  )
                }}
              </label>
            </div>
          </div>

          <div
            v-if="splitMethod === 'chunk'"
            class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4"
          >
            <div>
              <label class="block text-sm font-medium mb-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_WORD_SIZE_LABEL') }}</label>
              <input
                type="number"
                min="75"
                max="6000"
                v-model="chunkWordSize"
                @change="syncAdvancedUploadOptions"
                class="w-full"
              />
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_WORD_SIZE_HINT') }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_OVERLAP_LABEL') }}</label>
              <input
                type="number"
                min="0"
                max="1500"
                v-model="chunkOverlapWordSize"
                @change="syncAdvancedUploadOptions"
                class="w-full"
              />
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_OVERLAP_HINT') }}</p>
              <p v-if="isChunkOverlapInvalid" class="text-xs text-red-500 mt-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_OVERLAP_VALIDATION_ERROR') }}</p>
            </div>
          </div>

          <div v-else class="mt-4">
            <label class="block text-sm font-medium mb-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.DELIMITER_LABEL') }}</label>
            <input
              type="text"
              v-model="delimiter"
              @change="syncAdvancedUploadOptions"
              class="w-32"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.DELIMITER_HINT') }}</p>
            <p v-if="isDelimiterInvalid" class="text-xs text-red-500 mt-1">{{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.DELIMITER_VALIDATION_ERROR') }}</p>
          </div>
        </div>
      </div>

      <!-- File Upload Option -->
      <div class="my-6" v-if="source === 'file'">        
        <h2 class="text-lg mb-3">{{ $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.UPLOAD_FILE_SECTION') }}</h2>
        <DragAndDrop />
        <div class="w-full flex justify-between mt-8">
          <woot-button
            variant="smooth"
            color-scheme="alert"
            icon="dismiss-circle"
            @click="handleCancel"
            data-testid="upload_dialog_cancel_button"
          >
            {{ $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.CANCEL_BUTTON') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="file-upload"
            @click="handleUploadLocal"
            :disabled="(!uploadedFile && (!uploadedFiles || uploadedFiles.length === 0)) || isChunkOverlapInvalid || isDelimiterInvalid"
            data-testid="upload_dialog_upload_button"
          >
            <span v-html="isUploading ? `<span class='animate-pulse'>${$t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.LOADING')}</span>` : $t('KNOWLEDGE.UPLOADDOCUMENTDIALOG.UPLOAD_BUTTON')"></span>
          </woot-button>
        </div>
      </div>

      <!-- Google Drive Option -->
      <div class="my-6" v-if="source === 'gdrive'">
        <h2 class="text-lg mb-3">Google Drive</h2>
        <GoogleDrivePicker />
        <div class="w-full flex justify-between mt-8">
          <woot-button
            variant="smooth"
            color-scheme="alert"
            icon="dismiss-circle"
            @click="handleCancel"
          >
            Cancel
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="file-upload"
            @click="handleUploadLocal"
            :disabled="(!uploadedFile && (!uploadedFiles || uploadedFiles.length === 0)) || isChunkOverlapInvalid || isDelimiterInvalid"
          >
            <span v-html="isUploading ? '<span class=\'animate-pulse\'>Loading...</span>' : 'Upload'"></span>
          </woot-button>
        </div>
      </div>

      <!-- Website Crawl Option -->
      <div v-if="source === 'website'">
        <h2 class="text-lg mb-3">Website</h2>
        
        <div
          v-if="isExistingWordLimitExceeded"
          class="flex items-start gap-2 p-4 mb-4 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm"
        >
          <fluent-icon icon="warning" class="w-5 h-5 mt-[2px]" />
          <div>
            <p class="font-medium">
              Word limit already exceeded
            </p>
            <p class="mt-1">
              Your existing documents have already reached the maximum word limit. You cannot add more content at this time.
            </p>
          </div>
        </div>
        
        <FireCrawl @scrollDown="handleScrollDown" :disabled="isExistingWordLimitExceeded" />
        
        <div v-if="crawlResult && crawlResult.data && crawlResult.data.length" class="text-sm text-gray-600 mb-3">
          {{ selectedWordCount.toLocaleString() }} / {{ (wordLimit).toLocaleString() }} words used
        </div>

        <div
          v-if="isWordLimitExceeded"
          class="flex items-start gap-2 p-4 mb-4 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm"
        >
          <fluent-icon icon="warning" class="w-5 h-5 mt-[2px]" />
          <div>
            <p class="font-medium">
              Word limit reached
            </p>
            <p class="mt-1">
              You've reached the maximum allowed word limit.
              Deselect some content to select more results.
            </p>
          </div>
        </div>
        
        <!-- Crawl results selection -->
        <div ref="resultsList" v-if="crawlResult && crawlResult.data && crawlResult.data.length" class="space-y-4 break-all mt-4 max-h-[300px] overflow-y-auto">
          <div
            v-for="(result, index) in crawlResult.data"
            :key="resultKey(result, index)"
            class="border overflow-hidden border-gray-300 rounded-lg shadow-sm p-6 flex flex-col gap-3 transition-shadow duration-300"
            :class="{
              'opacity-50 cursor-not-allowed': !canSelect(result),
              'hover:shadow-md': canSelect(result)
            }"
          >
            <div class="flex items-start gap-4">
              <input
                type="checkbox"
                v-model="selectedResult"
                :value="result"
                class="w-5 h-5 mt-1 text-blue-600 rounded focus:ring-2 focus:ring-blue-400"
                :disabled="!canSelect(result) || isEditing(index)"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between">
                  <div class="min-w-0">
                    <h3 class="text-lg font-semibold text-gray-800 dark:text-slate-200 truncate">{{ result.metadata.title }}</h3>
                    <p class="text-sm text-gray-500 dark:text-slate-400 mb-1 truncate">{{ result.metadata.description }}</p>
                  </div>
                  <woot-button 
                    variant="clear" 
                    icon="edit" 
                    @click.stop="toggleExpanded(index)"
                    :aria-expanded="!!expanded[index]"
                    title="Expand to edit"
                  > {{ expanded[index] ? 'Close' : 'Edit' }}</woot-button>
                </div>

                <div class="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>Word count: {{ currentWordCount(index) }}</span>
                </div>

                <hr class="my-2 border-gray-200 dark:border-slate-600" />

                <p v-if="!expanded[index]" class="line-clamp-3 text-gray-700 dark:text-slate-300 text-sm whitespace-pre-wrap break-words">
                  {{ result.markdown }}
                </p>

                <div v-else class="w-full">
                  <textarea
                    v-model="editContent[index]"
                    class="w-full p-3 rounded border border-gray-200
                      focus:outline-none focus:ring-2 focus:ring-blue-200
                      text-sm text-gray-800 whitespace-pre-wrap
                      h-48 resize-none"
                    @click.stop
                  ></textarea>

                  <div class="mt-2 flex items-center gap-2">
                    <button
                      class="px-3 py-1 rounded border text-sm"
                      @click.stop="cancelEdit(index)"
                      :disabled="saving[index]"
                    >
                      Cancel
                    </button>

                    <button
                      class="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                      @click.stop="saveEdit(index)"
                      :disabled="saving[index]"
                    >
                      {{ saving[index] ? 'Saving...' : 'Save' }}
                    </button>

                    <span class="ml-auto text-xs text-gray-500">Chars: {{ editContent[index] ? editContent[index].length : 0 }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="w-full flex justify-between mt-8">
          <woot-button
            variant="smooth"
            color-scheme="alert"
            icon="dismiss-circle"
            @click="handleCancel"
          >
            Cancel
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="file-upload"
            @click="handleUploadCrawl"
            :disabled="!selectedResult || selectedResult.length === 0 || isChunkOverlapInvalid || isDelimiterInvalid"
          >
            <span v-html="isUploading ? '<span class=\'animate-pulse\'>Loading...</span>' : 'Upload Selected Pages'"></span>
          </woot-button>
        </div>
      </div>

      <!-- Deep Crawl Option -->
      <div v-if="source === 'deepcrawl'">
        <h2 class="text-lg mb-3">Website</h2>
        
        <div
          v-if="isExistingWordLimitExceeded"
          class="flex items-start gap-2 p-4 mb-4 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm"
        >
          <fluent-icon icon="warning" class="w-5 h-5 mt-[2px]" />
          <div>
            <p class="font-medium">
              Word limit already exceeded
            </p>
            <p class="mt-1">
              Your existing documents have already reached the maximum word limit. You cannot add more content at this time.
            </p>
          </div>
        </div>
        
        <DeepCrawl @scrollDown="handleScrollDown" :disabled="isExistingWordLimitExceeded" />
        
        <div v-if="crawlResult && crawlResult.data && crawlResult.data.length" class="text-sm text-gray-600 mb-3">
          {{ selectedWordCount.toLocaleString() }} / {{ (wordLimit).toLocaleString() }} words used
        </div>

        <div
          v-if="isWordLimitExceeded"
          class="flex items-start gap-2 p-4 mb-4 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm"
        >
          <fluent-icon icon="warning" class="w-5 h-5 mt-[2px]" />
          <div>
            <p class="font-medium">
              Word limit reached
            </p>
            <p class="mt-1">
              You've reached the maximum allowed word limit.
              Deselect some content to select more results.
            </p>
          </div>
        </div>
        
        <!-- Crawl results selection -->
        <div ref="resultsList" v-if="crawlResult && crawlResult.data && crawlResult.data.length" class="space-y-4 break-all mt-4 max-h-[300px] overflow-y-auto">
          <div
            v-for="(result, index) in crawlResult.data"
            :key="resultKey(result, index)"
            class="border overflow-hidden border-gray-300 rounded-lg shadow-sm p-6 flex flex-col gap-3 transition-shadow duration-300"
            :class="{
              'cursor-not-allowed': !canSelect(result),
              'hover:shadow-md': canSelect(result)
            }"
          >
            <div class="flex items-start gap-4">
              <div
                class="mt-1"
                :class="!canSelect(result) ? 'opacity-50' : ''"
              >
                <input
                  type="checkbox"
                  v-model="selectedResult"
                  :value="result"
                  class="w-5 h-5 mt-1 text-blue-600 rounded focus:ring-2 focus:ring-blue-400"
                  :class="{
                    'cursor-not-allowed': !canSelect(result),
                    'hover:shadow-md': canSelect(result)
                  }"
                  :disabled="!canSelect(result) || isEditing(index)"
                />
              </div>
              
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between">
                  <div class="min-w-0">
                    <h3 class="text-lg font-semibold text-gray-800 dark:text-slate-200 truncate">{{ result.metadata.title }}</h3>
                    <p class="text-sm text-gray-500 dark:text-slate-400 mb-1 truncate">{{ result.metadata.description }}</p>
                  </div>
                  <woot-button 
                    variant="clear" 
                    icon="edit" 
                    @click.stop="toggleExpanded(index)"
                    :aria-expanded="!!expanded[index]"
                    title="Expand to edit"
                  > {{ expanded[index] ? 'Close' : 'Edit' }}</woot-button>
                </div>

                <div class="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>Word count: {{ currentWordCount(index) }}</span>
                </div>

                <p
                  v-if="!canSelect(result) && !isWordLimitExceeded"
                  class="text-xs text-red-500 flex items-center gap-1 mt-1"
                >
                  <fluent-icon icon="info" size="12" />
                  Cannot select: exceeds allowed word limit. You can still edit this chunk to make it selectable.
                </p>

                <hr class="my-2 border-gray-200 dark:border-slate-600" />

                <p v-if="!expanded[index]" class="line-clamp-3 text-gray-700 dark:text-slate-300 text-sm whitespace-pre-wrap break-words">
                  {{ result.markdown }}
                </p>

                <div v-else class="w-full">
                  <textarea
                    v-model="editContent[index]"
                    class="w-full p-3 rounded border border-gray-200
                      focus:outline-none focus:ring-2 focus:ring-blue-200
                      text-sm text-gray-800 whitespace-pre-wrap
                      h-48 resize-none"
                    @click.stop
                  ></textarea>

                  <div class="mt-2 flex items-center gap-2">
                    <button
                      class="px-3 py-1 rounded border text-sm"
                      @click.stop="cancelEdit(index)"
                      :disabled="saving[index]"
                    >
                      Cancel
                    </button>

                    <button
                      class="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                      @click.stop="saveEdit(index)"
                      :disabled="saving[index]"
                    >
                      {{ saving[index] ? 'Saving...' : 'Save' }}
                    </button>

                    <span class="ml-auto text-xs text-gray-500">Chars: {{ editContent[index] ? editContent[index].length : 0 }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="w-full flex justify-between mt-8">
          <woot-button
            variant="smooth"
            color-scheme="alert"
            icon="dismiss-circle"
            @click="handleCancel"
          >
            Cancel
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="file-upload"
            @click="handleUploadCrawl"
            :disabled="!selectedResult || selectedResult.length === 0 || isChunkOverlapInvalid || isDelimiterInvalid"
          >
            <span v-html="isUploading ? '<span class=\'animate-pulse\'>Loading...</span>' : 'Upload Selected Pages'"></span>
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>